import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import {
  AlarmActivation,
  AlarmEvent,
  alarmEventFromJSON,
  AlarmEventType,
  Severity,
} from '@repo/sdk/models/components';

import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useGaiaClient } from '@/hooks/use-gaia-client';
import { useStream } from '@/hooks/use-stream';
import { activeAlarmActivationsQuery } from '@/queries/gaia-api/active-alarm-activations-query';
import { alarmActivationsSummaryQuery } from '@/queries/gaia-api/alarm-activations-summary-query';
import { listHistoricalAlarmActivationsInfiniteQuery } from '@/queries/gaia-api/list-historical-alarm-activations-infinite-query';
import { STREAM_TYPES } from '@/state/stream-status.state';

const HISTORICAL_INVALIDATE_DEBOUNCE_MS = 400;

// Check to see if any active alarms have been resolved or cleared
const checkForInactivations = (activations: AlarmActivation[]) => {
  for (const activation of activations) {
    if (
      // If the alarm is critical and has been cleared, it is resolved
      // If the alarm is warning and has been resolved, it is resolved
      // If the alarm is caution and has been resolved, it is resolved
      (activation.severity == Severity.Critical && !!activation.clearedAt) ||
      (activation.severity == Severity.Warning && !!activation.resolvedAt) ||
      (activation.severity == Severity.Caution && !!activation.resolvedAt)
    ) {
      return true;
    }
  }
  return false;
};

export const useAlarmsStream = () => {
  const { agentId } = useCurrentFacility();
  const client = useGaiaClient();

  const queryClient: QueryClient = useQueryClient();
  const {
    webSocketUrl,
    queryKey: alarmActivationsKey,
    enabled,
  } = activeAlarmActivationsQuery({ agentId });
  const { queryKey: summaryKey } = alarmActivationsSummaryQuery({ agentId });
  const { queryKey: historicalKey } = listHistoricalAlarmActivationsInfiniteQuery({
    agentId,
    client,
  });

  const scheduleHistoricalInvalidation = useMemo(
    () =>
      debounce(() => {
        queryClient.invalidateQueries({ queryKey: historicalKey });
      }, HISTORICAL_INVALIDATE_DEBOUNCE_MS),
    [queryClient, historicalKey]
  );

  useEffect(
    () => () => {
      scheduleHistoricalInvalidation.cancel();
    },
    [scheduleHistoricalInvalidation]
  );

  const handleAlarmEvent = useCallback(
    (event: AlarmEvent) => {
      switch (event.type) {
        case AlarmEventType.Keepalive: {
          // Keep Alive is a keep alive message.
          // Maybe: A single keepalive cache for all sockets?
          console.debug(`AlarmActivation keep alive at ${new Date().toISOString()}`);
          break;
        }
        case AlarmEventType.AlarmActivation: {
          // Alarm Activation is a list of alarm activations.
          queryClient.setQueryData(alarmActivationsKey, (old: AlarmActivation[]) => {
            const activationIds = new Set((event.activations || []).map((a) => a.activationId));
            const filtered = (old || []).filter((a) => !activationIds.has(a.activationId));
            const allActivations = [...filtered, ...(event.activations || [])];
            return allActivations;
          });

          // Update the summary query data.
          queryClient.setQueryData(summaryKey, () => event.summary);

          // If any activations have been resolved or cleared, refresh historical (debounced: stream can burst)
          if (checkForInactivations(event.activations || [])) {
            scheduleHistoricalInvalidation();
          }

          break;
        }
        default:
          break;
      }
    },
    [queryClient, alarmActivationsKey, summaryKey, scheduleHistoricalInvalidation]
  );

  return useStream<AlarmEvent>(enabled ? webSocketUrl : null, {
    onReceiveContent: handleAlarmEvent,
    parse: alarmEventFromJSON,
    streamType: STREAM_TYPES.ALARMS,
    displayName: 'Alarms',
  });
};
